/**
 * @file Account related helper functions.
 */

import get from 'lodash.get';
import uniqBy from 'lodash.uniqby';

import { isAlbum } from './album';
import { isAlbumFolder } from './album-folder';
import ACCOUNT_ROLES, {
  ACCOUNT_PRO_ROLES,
  ACCOUNT_DASHBOARD_ROLES,
} from '../constants/account-roles';
import {
  accountEmailExists,
  accountPhoneNumberExists,
} from '../graphql/queries/account';

/**
 * Return an account album membership by album id.
 *
 * @param {Object} account The account record.
 * @param {String} albumId The album to filter by.
 * @returns {Object|undefined} The album membership record if found.
 */
const getAlbumMembership = (account, albumId) => {
  if (!account) {
    return null;
  }

  return get(account, 'album_memberships.records', []).find(
    record => record.album.id === albumId,
  );
};

/**
 * Return an account album membership by album membership id.
 *
 * @param {Object} account The account record.
 * @param {String} albumMembershipId The album membership to filter by.
 * @returns {Object|undefined} The album membership record if found.
 */
const getAlbumMembershipById = (account, albumMembershipId) => {
  if (!account) {
    return null;
  }

  return get(account, 'album_memberships.records', []).find(
    record => record.id === albumMembershipId,
  );
};

/**
 * Return purchased identities records for a specific album membership.
 *
 * @param {Object} account The account record.
 * @param {String} albumId The album to filter by.
 * @returns {Array<Object>} The purchased identities records.
 */
const getPurchasedIdentities = (account, albumId) => {
  if (!account) {
    return [];
  }

  const albumMembership = getAlbumMembership(account, albumId);
  return get(albumMembership, 'purchased_identities.records', []);
};

/**
 * Return selected identities records for a specific album membership.
 *
 * @param {Object} account The account record.
 * @param {String} albumId The album to filter by.
 * @returns {Array<Object>} The purchased identities records.
 */
const getSelectedIdentities = (account, albumId) => {
  if (!account) {
    return null;
  }

  const albumMembership = getAlbumMembership(account, albumId);
  return get(albumMembership, 'selected_identities.records', []);
};

/**
 * Return purchased identities records for a specific folder.
 *
 * @param {Object} account The account record.
 * @param {String} folderId Folder to filter by.
 * @returns {Array<Object>} The purchased identities records.
 */
const getPurchasedIdentitiesOnFolder = (account, folderId) => {
  if (!account) {
    return [];
  }

  const identities = get(account, 'linked_identities.records', []);
  return identities.filter(identity => {
    const purchasedFolders = get(
      identity,
      'purchased_album_folders.records',
      [],
    );
    return purchasedFolders.some(folder => folder.id === folderId);
  });
};

/**
 * Return identity record from account identities.
 *
 * @param {Object} account The account record.
 * @param {String} identityId The identity id to check.
 * @returns {Object|undefined} The identity object if found.
 */
const getIdentity = (account, identityId) => {
  if (!account) {
    return null;
  }

  return account.linked_identities.records.find(
    record => record.id === identityId,
  );
};

/**
 * Check input identities against user identities.
 * Returns true if all identities are valid.
 *
 * @param {Array.<String>} identities The identities to check.
 * @param {Array.<Object>} userIdentities The user identities to check against.
 * @returns {Boolean} The validation result.
 */
const areValidIdentities = (identities = [], userIdentities = []) => {
  return !identities.some(
    identity =>
      !userIdentities.find(userIdentity => userIdentity.id === identity),
  );
};

/**
 * Check if account has a specific role.
 *
 * @param {Object} account The account record.
 * @param {String} role The role to check.
 * @returns {Boolean} If user has the role.
 */
const hasRole = (account, role) => {
  return account.roles.includes(role);
};

/**
 * Return the account display handle.
 *
 * @param {Object} account The account record.
 * @returns {String} The account handle.
 */
const getAccountDisplayHandle = account => {
  if (!account) {
    return '';
  }

  const {
    username,
    email_address: emailAddress,
    phone_number: phoneNumber,
  } = account;

  if (username) {
    return `@${username}`;
  }

  return emailAddress || phoneNumber || '';
};

const getAccountName = account => {
  return account.name || '';
};

const getAccountUsername = account => {
  return account?.username || '';
};

const getAccountLastDigits = account => {
  return account.phone_number ? `...${account.phone_number.slice(-4)}` : '';
};

/**
 * Returns the handle of a content (album) creator that will
 * be displayed for users in checkout screens.
 *
 * @param {Object} account - The content creators account.
 * @returns {String} - The name.
 */
const getAccountCheckoutGreetingName = account => {
  if (!account) {
    return undefined;
  }

  return account.organization_name || account.name;
};

/**
 * Returns the Account organization name or a default value.
 *
 * @param {Object} account The content creators account.
 * @param {String} defaultValue Default value to return if no org. Name is found.
 * @returns {String} The name.
 */
const getAccountOrganizationName = (account, defaultValue = 'Organization') => {
  return get(account, 'organization_name') || defaultValue;
};

/**
 * Gets a url and an entity (Album or AlbumFolder) and
 * returns the correct URL by prepending the account slug found
 * on the entity (if any), otherwise returns the URL.
 *
 * @param {String} url The original, bare url.
 * @param {Object} entity The album or album folder with the account slug field.
 * @returns {String} The url prepended with the user slug (or not).
 */
const prependAccountSlug = (url, entity) => {
  if (!entity) {
    return url;
  }

  let slug = isAlbum(entity) ? get(entity, 'creator.slug') : null;
  slug = isAlbumFolder(entity) ? get(entity, 'account.slug') : slug;

  return slug ? `/${slug}${url}` : url;
};

/**
 * Checks if the user is an administrator.
 *
 * @param {Object} user The account object.
 * @returns {Boolean} If the user is admin or not.
 */
const isAdmin = user => {
  if (!user) {
    return false;
  }

  return get(user, 'roles', []).includes(ACCOUNT_ROLES.ADMINISTRATOR);
};

/**
 * Checks if the user has the Shutterbug role.
 *
 * @param {Object} user The account object.
 * @returns {boolean}
 */
const isShutterbug = user => {
  if (!user) {
    return false;
  }

  return (user?.roles ?? []).includes(ACCOUNT_ROLES.SHUTTERBUG);
};

/**
 * Checks to see if a user should be considered only a commander by the app.
 * The user is considered to be just a commander when he does not have any
 * pro account roles, i.e. In the following role combinations.
 *
 * `COMMANDER`
 * `CONSUMER+COMMANDER`
 * `CONSUMER+COMMANDER+ADMINISTRATOR`
 * `COMMANDER+ADMINISTRATOR`.
 *
 * This allows for a common signin from Waldo to everyone without
 * having to type URLs manually.
 *
 * Mainly used in `EntryForward` which is responsible to redirect users
 * to the correct place in the app after sign in.
 *
 * @param {ACCOUNT_ROLES} roles The user's roles.
 * @returns {Boolean} Whether the user is considered only a commander.
 */
const isCommanderOnly = roles => {
  // If the user has any pro roles he's not considered only a commander
  if (
    !roles ||
    roles.some(role => Object.values(ACCOUNT_PRO_ROLES).includes(role))
  ) {
    return false;
  }

  return roles.includes(ACCOUNT_ROLES.COMMANDER);
};

/**
 * Used to display the publicly facing identity of a user.
 * Either as a gallery creator/owner or contributor.
 *
 * @param {Object} [account] The account.
 * @returns {string}
 */
const getShortenedAccountName = (account = {}) => {
  // Should return based on a hierarchy of what is available.
  // 1. Username, 2. formatted name (J. Smith), 3. [blank string]
  const { name, username } = account;

  if (username) {
    return `@${username}`;
  }

  if (!name) {
    return '';
  }

  const [firstName, ...rest] = name.trim().split(' ');

  if (rest.length) {
    // Get the first character of the last element of the array.
    const last = rest.slice(-1)[0].charAt(0);

    return `${firstName} ${last}.`;
  }

  return firstName.trim();
};

/**
 * Determines whether a provided username is valid.
 *
 * @param {string} username The username.
 * @returns {boolean}
 */
const isValidUsername = username => {
  if (!username) {
    return false;
  }

  return !!username.match(/^\w{3,}$/);
};

/**
 * Determines whether a user is a PRO user or not based on its roles.
 *
 * @param {Object} account The user account.
 */
const isProAccount = account => {
  const proRoles = Object.values(ACCOUNT_PRO_ROLES);

  return !!(account?.roles || []).find(role =>
    proRoles.includes(role.toLowerCase()),
  );
};

/**
 * Returns the default PRO role for a certain account.
 *
 * @param {Object} account The account to get the role for.
 */
const getDefaultProRoleFromAccount = account => {
  if (!account?.roles) {
    return undefined;
  }
  // Prefer roles for Camp > School > PRO > Shutterbug and redirect to that basename.
  const sortedAccountRoles = uniqBy([
    ACCOUNT_DASHBOARD_ROLES.CAMP_ADMIN,
    ACCOUNT_DASHBOARD_ROLES.SCHOOL_ADMIN,
    ACCOUNT_DASHBOARD_ROLES.GREEK_ADMIN,
    ACCOUNT_DASHBOARD_ROLES.PHOTOGRAPHER,
    ...Object.values(ACCOUNT_DASHBOARD_ROLES),
  ]);

  const sortedUserRoles = [...account.roles].sort((a, b) => {
    const indexA = sortedAccountRoles.indexOf(a);
    const indexB = sortedAccountRoles.indexOf(b);

    if (indexA === -1 && indexB !== -1) {
      return 1;
    }

    if (indexA !== -1 && indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });

  return sortedUserRoles.find(x =>
    Object.values(ACCOUNT_DASHBOARD_ROLES).includes(x),
  );
};

/**
 * Determines if an address is valid or not.
 *
 * @param {Object} address The Address to verify.
 * @returns {boolean}
 */
const isAddressValid = address => {
  return !(
    !address.name ||
    !address.address_line_1 ||
    !address.city ||
    !address.post_code ||
    !address.state
  );
};

/**
 * Checks if the user is the creator/owner of the albumFolder, returns false if params are undefined.
 *
 * @param {Object} user The account object.
 * @param {Object} albumFolder Requires albumFolder.account.id to be effective.
 * @returns {Boolean}
 */
export const isAlbumFolderOwner = (user, albumFolder) => {
  const account = albumFolder?.creator || albumFolder?.account;
  return account?.id && user?.id ? account.id === user.id : false;
};

/**
 * Checks if the user is the creator/owner of the album, returns false if params are undefined.
 *
 * @param {Object} user The account object.
 * @param {Object} album Requires album.creator.id to be effective.
 * @returns {Boolean}
 */
export const isAlbumOwner = (user, album) => {
  const account = album?.creator || album?.account;
  return account?.id && user?.id ? account.id === user.id : false;
};

/**
 * Checks whether the email or phone number passed into the method is available for use.
 *
 * @param {Object} opts
 * @param {Object} opts.client Apollo client object.
 * @param {String} [opts.email] Email address to check.
 * @param {String} [opts.phoneNumber] Phone number to check.
 * @returns {Promise<{{ exists: boolean, isVerified: boolean }}>} Whether the auth method exists on the platform or not and is verified.
 */
const accountAuthMethodExists = async ({ client, email, phoneNumber }) => {
  if (email) {
    const {
      data: {
        accountEmailExists: { exists, is_verified: isVerified },
      },
    } = await client.query({
      query: accountEmailExists,
      variables: {
        email_address: email,
      },
      fetchPolicy: 'network-only',
    });

    return { exists, isVerified };
  }
  // check phone:
  const {
    data: {
      accountPhoneNumberExists: { exists },
    },
  } = await client.query({
    query: accountPhoneNumberExists,
    variables: {
      phone_number: phoneNumber,
    },
    fetchPolicy: 'network-only',
  });

  return { exists };
};

/**
 * Gets the type of content the account has active (owned, shared, joined).
 *
 * @param {Object} account The account to check.
 * @returns {{ hasOwnedContent: boolean, hasSharedContent: boolean, hasJoinedContent: boolean }}
 */
const getAccountContent = account => {
  const hasOwnedContent = (account?.album_folders.total ?? 0) > 0;
  const hasSharedContent =
    (account?.contributor_hierarchy_items.total ?? 0) > 0;
  const hasJoinedContent = account?.has_joined_content;

  return { hasOwnedContent, hasSharedContent, hasJoinedContent };
};

/**
 * Checks whether the Account meets the criteria to join the Albums
 * or AlbumFolders.
 *
 * @param {Object} account The account to check for.
 * @returns {boolean}
 */
const canSkipCheckout = account => {
  return account?.search_subscriptions?.total > 0;
};

/**
 * Determines whether an Account has no created items (is in null state).
 *
 * @param {Object} account Account.
 * @returns {boolean}
 */
const accountHasNullState = account => {
  return account?.album_folders?.total === 0 && account?.albums?.total === 0;
};

/**
 * Determines whether an Account has access to social features or not
 *
 * @param {Object} account Account
 */
const hasSocialFeaturesAccess = account => {
  return isShutterbug(account);
};

/**
 * Used to display attribution on photo and videos.
 * Should return based on a hierarchy of what is available.
 * 1. orgName, 2. Name, 3. Username
 *
 * @param {Object} [account] The account.
 * @returns {string}
 */
const getAttributionName = (account = {}) => {
  const { name, username, organization_name: organizationName } = account;

  if (organizationName) {
    return organizationName;
  }

  if (!name && username) {
    return `@${username}`;
  }

  if (!name) {
    return '';
  }

  const [firstName, ...rest] = name.trim().split(' ');

  if (rest.length) {
    // Get the first character of the last element of the array.
    const last = rest.slice(-1)[0].charAt(0);

    return `${firstName} ${last}.`;
  }

  return firstName.trim();
};

export {
  getAlbumMembership,
  getAlbumMembershipById,
  getPurchasedIdentities,
  getIdentity,
  areValidIdentities,
  hasRole,
  getAccountDisplayHandle,
  getAccountCheckoutGreetingName,
  prependAccountSlug,
  isAdmin,
  isCommanderOnly,
  getPurchasedIdentitiesOnFolder,
  getSelectedIdentities,
  getAccountName,
  getAccountUsername,
  getAccountLastDigits,
  getAccountOrganizationName,
  getShortenedAccountName,
  isValidUsername,
  isProAccount,
  getDefaultProRoleFromAccount,
  isAddressValid,
  accountAuthMethodExists,
  getAccountContent,
  isShutterbug,
  canSkipCheckout,
  accountHasNullState,
  hasSocialFeaturesAccess,
  getAttributionName,
};
